'use client';

import { useCallback, useRef } from 'react';

interface UseMultiTapTriggerOptions {
    requiredTaps: number;
    intervalMs: number;
    onTrigger: () => void;
}

/**
 * Generic hook to detect multiple taps/clicks within a given interval.
 *
 * Example:
 * const handleTap = useMultiTapTrigger({
 *   requiredTaps: 5,
 *   intervalMs: 2000,
 *   onTrigger: () => { ... },
 * });
 *
 * <div onClick={handleTap}>Secret area</div>
 */
export function useMultiTapTrigger({
    requiredTaps,
    intervalMs,
    onTrigger,
}: UseMultiTapTriggerOptions) {
    const tapCountRef = useRef(0);
    const lastTapTimeRef = useRef<number | null>(null);

    const reset = useCallback(() => {
        tapCountRef.current = 0;
        lastTapTimeRef.current = null;
    }, []);

    return useCallback(() => {
        const now = Date.now();

        if (
            lastTapTimeRef.current === null ||
            now - lastTapTimeRef.current > intervalMs
        ) {
            // First tap or taps too far apart — start a new sequence.
            tapCountRef.current = 1;
            lastTapTimeRef.current = now;
            return;
        }

        tapCountRef.current += 1;
        lastTapTimeRef.current = now;

        if (tapCountRef.current >= requiredTaps) {
            reset();
            onTrigger();
        }
    }, [intervalMs, onTrigger, requiredTaps, reset]);
}
